<template>
  <div class="min-h-screen relative overflow-x-hidden text-slate-800 font-NotoSerifKhmer">
    <!-- Animated Background -->
    <div class="fixed inset-0 bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-500 bg-[length:400%_400%] animate-gradient -z-20"></div>
    <div class="fixed inset-0 -z-10 bg-[radial-gradient(circle_at_20%_50%,rgba(255,107,157,0.3)_0%,transparent_50%),radial-gradient(circle_at_80%_80%,rgba(102,126,234,0.3)_0%,transparent_50%),radial-gradient(circle_at_40%_20%,rgba(67,233,123,0.2)_0%,transparent_50%)]"></div>

    <!-- Navigation Header -->
    <nav class="sticky top-0 z-40 backdrop-blur-xl bg-white/10 border-b border-white/20 mb-8 transition-all duration-300">
      <div class="max-w-[1400px] mx-auto px-4 md:px-8">
        <div class="flex justify-between items-center h-16">
          <!-- Logo/Brand -->
          <router-link to="/" class="flex items-center gap-2 cursor-pointer">
            <span class="text-2xl animate-bounce-slow">💍</span>
            <span class="text-white font-bold text-lg hidden md:block tracking-tight">{{ t('Wedding Ledger') }}</span>
          </router-link>
          
          <!-- Actions Area -->
          <div class="flex items-center gap-3 md:gap-4">
            <!-- Telegram Contact Button -->
            <a 
              href="https://t.me/Chamroeun_Pich_Samphors" 
              target="_blank"
              class="flex items-center gap-2 px-3 py-2 bg-sky-500/20 hover:bg-sky-500/30 text-sky-400 rounded-xl transition-all duration-300 border border-sky-400/20 shadow-lg group"
              title="Contact Support"
            >
              <svg class="w-5 h-5 transition-transform group-hover:scale-110" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.11.02-1.93 1.23-5.46 3.62-.51.35-.98.52-1.4.51-.46-.01-1.35-.26-2.01-.48-.81-.27-1.45-.42-1.39-.88.03-.24.36-.49 1-.74 3.91-1.7 6.52-2.82 7.83-3.36 3.73-1.54 4.5-1.81 5.01-1.82.11 0 .35.03.5.16.13.11.17.26.18.37 0 .04.01.12 0 .2z"/>
              </svg>
              <span class="text-xs font-bold hidden lg:block tracking-wide uppercase">{{ t('Contact Us') }}</span>
            </a>

            <!-- Subscription Status -->
            <div v-if="userSubscription" class="flex items-center gap-2 px-2 py-1 md:px-3 md:py-1.5 bg-white/5 border border-white/10 rounded-xl backdrop-blur-md">
              <div class="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
              <span class="text-[10px] text-white/60 font-medium uppercase tracking-widest hidden xs:block">{{ t('Active Plan:') }}</span>
              <span class="text-xs text-white font-bold">{{ userSubscription.name }}</span>
            </div>

            <!-- Upgrade Button -->
            <button 
              @click="openSubscription"
              class="group relative overflow-hidden flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-300 to-yellow-500 rounded-xl text-white font-bold text-sm shadow-[0_4px_12px_rgba(245,158,11,0.4)] transition-all duration-300 hover:scale-105 hover:shadow-[0_6px_20px_rgba(245,158,11,0.6)]"
            >
              <div class="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
              <span class="relative">{{ t('Upgrade') }}</span>
            </button>

            <!-- Profile Dropdown -->
            <div class="relative" ref="profileDropdown">
              <button 
                @click="toggleProfileMenu"
                class="flex items-center gap-3 px-2 py-1.5 md:px-3 md:py-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-300 border border-white/20"
              >
                <div class="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm ring-2 ring-white/20">
                  {{ userInitials }}
                </div>
                <span class="text-white font-semibold hidden md:block text-sm">{{ userName }}</span>
                <svg class="w-4 h-4 text-white transition-transform duration-300 hidden md:block" :class="{ 'rotate-180': showProfileMenu }" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
              </button>
              
              <!-- Dropdown Menu -->
              <transition
                enter-active-class="transition ease-out duration-200"
                enter-from-class="opacity-0 scale-95 translate-y-2"
                enter-to-class="opacity-100 scale-100 translate-y-0"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="opacity-100 scale-100 translate-y-0"
                leave-to-class="opacity-0 scale-95 translate-y-2"
              >
                <div v-if="showProfileMenu" class="absolute right-0 mt-3 w-64 bg-white/10 backdrop-blur-2xl rounded-2xl border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.3)] overflow-hidden z-50">
                  <div class="p-5 border-b border-white/10 bg-white/5">
                    <div class="text-white font-bold text-lg">{{ userName }}</div>
                    <div class="text-white/60 text-xs mt-1">{{ userEmail }}</div>
                  </div>
                  <div class="p-2">
                    <button @click="updateProfile" class="w-full flex items-center gap-3 px-4 py-3 text-white/90 hover:bg-white/10 hover:text-white rounded-xl transition-all duration-200 text-left text-sm font-medium">
                      <svg class="w-5 h-5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                      {{ t('Update Profile') }}
                    </button>

                    <!-- Language Selector -->
                    <div class="mt-2 px-3 pb-2">
                      <div class="text-[10px] text-white/40 font-bold uppercase tracking-widest px-1 mb-2">{{ t('Select Language') }}</div>
                      <div class="grid grid-cols-1 gap-1">
                        <button 
                          v-for="lang in languages" 
                          :key="lang.code"
                          @click="onLanguageChange(lang.code)"
                          class="flex items-center justify-between px-3 py-2 rounded-xl transition-all duration-200 group/lang"
                          :class="selectedLang === lang.code ? 'bg-white/20 text-white' : 'text-white/60 hover:bg-white/5 hover:text-white/90'"
                        >
                          <div class="flex items-center gap-3">
                            <img :src="lang.flag" :alt="lang.name" class="w-5 h-3.5 rounded-sm object-cover shadow-sm" />
                            <span class="text-sm font-medium">{{ lang.name }}</span>
                          </div>
                          <div v-if="selectedLang === lang.code" class="w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.5)]"></div>
                        </button>
                      </div>
                    </div>

                    <div class="h-px bg-white/10 my-1 mx-2"></div>
                    <button @click="logout" class="w-full flex items-center gap-3 px-4 py-3 text-rose-300 hover:bg-rose-500/20 hover:text-rose-200 rounded-xl transition-all duration-200 text-left text-sm font-medium">
                      <svg class="w-5 h-5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
                      {{ t('Logout') }}
                    </button>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- Main Container -->
    <main class="max-w-[1400px] mx-auto px-4 pb-12 md:px-8 transition-all duration-500">
     
        <router-view >
    
      </router-view>
  
    </main>

    <!-- Modern Glass Footer -->
    <footer class="relative mt-16 overflow-hidden">
      <!-- Backdrop Blur Layer -->
      <div class="absolute inset-0 backdrop-blur-2xl bg-gradient-to-t from-white/10 via-white/5 to-transparent border-t border-white/20 -z-10"></div>
      
      <!-- Animated Gradient Accent -->
      <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-purple-400/50 to-transparent animate-pulse"></div>
      
      <div class="max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-12">
        <!-- Footer Content Grid -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-8">
          
          <!-- Brand Section -->
          <div class="flex flex-col items-center md:items-start gap-3 group">
            <div class="flex items-center gap-2">
              <span class="text-3xl transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12">💍</span>
              <h3 class="text-white font-bold text-xl tracking-tight">{{ t('Wedding Ledger') }}</h3>
            </div>
            <p class="text-white/60 text-sm text-center md:text-left leading-relaxed max-w-xs">
              {{ t('Professional wedding guest management system for modern celebrations') }}
            </p>
          </div>

          <!-- Quick Links -->
          <div class="flex flex-col items-center gap-3">
            <h4 class="text-white/80 font-semibold text-sm uppercase tracking-widest">{{ t('Connect') }}</h4>
            <div class="flex gap-3">
              <!-- Telegram -->
              <a 
                href="https://t.me/Chamroeun_Pich_Samphors" 
                target="_blank"
                class="group/social w-10 h-10 flex items-center justify-center bg-white/5 hover:bg-sky-500/20 border border-white/10 hover:border-sky-400/30 rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-[0_4px_20px_rgba(56,189,248,0.3)]"
                title="Telegram"
              >
                <svg class="w-5 h-5 text-white/60 group-hover/social:text-sky-400 transition-colors" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.11.02-1.93 1.23-5.46 3.62-.51.35-.98.52-1.4.51-.46-.01-1.35-.26-2.01-.48-.81-.27-1.45-.42-1.39-.88.03-.24.36-.49 1-.74 3.91-1.7 6.52-2.82 7.83-3.36 3.73-1.54 4.5-1.81 5.01-1.82.11 0 .35.03.5.16.13.11.17.26.18.37 0 .04.01.12 0 .2z"/>
                </svg>
              </a>
            </div>

            <!-- Contact Info Cards -->
            <div class="flex flex-col gap-3 w-full max-w-[500px]">
              <!-- Email Display -->
              <div class="flex items-center gap-3 px-4 py-2 bg-white/5 border border-white/10 rounded-xl backdrop-blur-md transition-all duration-300 group/email">
                <div class="w-8 h-8 flex items-center justify-center bg-pink-500/20 rounded-lg text-pink-400 group-hover/email:scale-110 transition-transform">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                </div>
                <div class="flex flex-col">
                  <span class="text-[10px] text-white/40 font-bold uppercase tracking-widest">{{ t('Email') }}</span>
                  <span class="text-xs text-white/80 font-medium select-all">chamroeun.pichsamphors@gmail.com</span>
                </div>
              </div>

              <!-- Phone Display -->
              <div class="flex items-center gap-3 px-4 py-2 bg-white/5 border border-white/10 rounded-xl backdrop-blur-md transition-all duration-300 group/phone">
                <div class="w-8 h-8 flex items-center justify-center bg-cyan-500/20 rounded-lg text-cyan-400 group-hover/phone:scale-110 transition-transform">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                  </svg>
                </div>
                <div class="flex flex-col">
                  <span class="text-[10px] text-white/40 font-bold uppercase tracking-widest">{{ t('Phone') }}</span>
                  <span class="text-xs text-white/80 font-medium select-all">+855 87 344 479</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Version & Info -->
          <div class="flex flex-col items-center md:items-end gap-3">
            <div class="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl backdrop-blur-sm">
              <div class="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.5)]"></div>
              <span class="text-white/80 text-xs font-mono font-semibold">v0.1.0</span>
            </div>
            <div class="flex flex-wrap justify-center md:justify-end gap-2 text-[10px] text-white/50 uppercase tracking-wider">
              <span class="hover:text-white/80 transition-colors cursor-pointer">{{ t('Privacy') }}</span>
              <span class="text-white/30">•</span>
              <span class="hover:text-white/80 transition-colors cursor-pointer">{{ t('Terms') }}</span>
              <span class="text-white/30">•</span>
              <span class="hover:text-white/80 transition-colors cursor-pointer">{{ t('Support') }}</span>
            </div>
          </div>
        </div>

        <!-- Divider with Gradient -->
        <div class="relative h-px mb-6">
          <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
        </div>

        <!-- Copyright Section -->
        <div class="flex flex-col md:flex-row items-center justify-between gap-4">
          <div class="flex items-center gap-2 text-white/60 text-xs md:text-sm">
            <svg class="w-4 h-4 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
            <span class="font-medium">
              © {{ new Date().getFullYear() }} 
              <span class="text-white/80 font-semibold mx-1">Phors_Stephen</span>
              {{ t('All rights reserved') }}
            </span>
          </div>
          
          <!-- ISO Badge -->
          <div class="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-400/20 rounded-lg">
            <svg class="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            </svg>
            <span class="text-amber-300/90 text-[10px] font-bold uppercase tracking-widest">ISO Compliant</span>
          </div>
        </div>

        <!-- Tech Stack Badge (Optional) -->
        <!-- <div class="mt-6 flex justify-center">
          <div class="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-[10px] text-white/50 group/tech hover:bg-white/10 transition-all duration-300">
            <span>{{ t('Built with') }}</span>
            <div class="flex items-center gap-1.5">
              <span class="text-emerald-400 font-semibold group-hover/tech:text-emerald-300 transition-colors">Vue.js</span>
              <span class="text-white/30">•</span>
              <span class="text-orange-400 font-semibold group-hover/tech:text-orange-300 transition-colors">Firebase</span>
              <span class="text-white/30">•</span>
              <span class="text-sky-400 font-semibold group-hover/tech:text-sky-300 transition-colors">Tailwind CSS</span>
            </div>
          </div>
        </div> -->
      </div>

      <!-- Bottom Glow Effect -->
      <div class="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
    </footer>

    <!-- Modals -->
    <SubscriptionModal :isOpen="isSubscriptionOpen" :onClose="closeSubscription" />
    <ProfileModal 
      :isOpen="isProfileOpen" 
      :user="dataUser[0]" 
      :onClose="closeProfile" 
      @updated="fetchUserAuthentication" 
    />
    
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { signOut } from 'firebase/auth'
import { projectAuth } from '../firebase/config'
import { useRouter } from 'vue-router'
import { getCollectionQuery } from '@/composables/getCollection'
import { documentId, where } from 'firebase/firestore'
import SubscriptionModal from '@/components/Modal/SubscriptionModal.vue'
import ProfileModal from '@/components/Modal/ProfileModal.vue'
import { useI18n } from 'vue-i18n'
import khFlag from '@/assets/Cambodia.png'
import usaFlag from '@/assets/English.png'
import zhFlag from '@/assets/China.jpg'

// Routing & User Data
const router = useRouter()
const { t, locale } = useI18n()
const userName = ref('User')
const userEmail = ref('')
const dataUser = ref([])
const isSubscriptionOpen = ref(false)
const isProfileOpen = ref(false)

// Language Options
const languages = [
  { code: 'en', name: 'English', flag: usaFlag },
  { code: 'km', name: 'Khmer', flag: khFlag },
  // { code: 'zh', name: 'Chinese', flag: zhFlag },
]
const selectedLang = ref(locale.value)

const onLanguageChange = (code) => {
  locale.value = code
  selectedLang.value = code
  localStorage.setItem('locale', code)
}

// All Plans for Subscription Status
const allPlans = ref([])
const fetchAllPlans = async () => {
  try {
    await getCollectionQuery(
      "plans",
      [where("status", "==", true)],
      (data) => {
        allPlans.value = data
      },
      null,
      true
    )
  } catch (error) {
    console.error("failed to fetch plans", error)
  }
}

const userSubscription = computed(() => {
  return allPlans.value.find((plan) => plan.id === dataUser.value[0]?.planId)
})

// Modal Controls
const openSubscription = () => isSubscriptionOpen.value = true
const closeSubscription = () => isSubscriptionOpen.value = false

const openProfile = () => isProfileOpen.value = true
const closeProfile = () => isProfileOpen.value = false

// Profile Menu Logic
const showProfileMenu = ref(false)
const profileDropdown = ref(null)

const toggleProfileMenu = () => {
  showProfileMenu.value = !showProfileMenu.value
}

const handleClickOutside = (event) => {
  if (profileDropdown.value && !profileDropdown.value.contains(event.target)) {
    showProfileMenu.value = false
  }
}

const updateProfile = () => {
  showProfileMenu.value = false
  openProfile()
}

const logout = async () => {
  showProfileMenu.value = false
  await signOut(projectAuth)
  router.push("/login")
}

// User Initials
const userInitials = computed(() => {
  if (!userName.value) return '??'
  const names = userName.value.split(' ')
  return names.length >= 2 
    ? (names[0][0] + names[names.length - 1][0]).toUpperCase()
    : userName.value.substring(0, 2).toUpperCase()
})

// Fetch User
const fetchUserAuthentication = async() => {
  if (projectAuth.currentUser) {
    await getCollectionQuery("users", [where(documentId(), "==", projectAuth.currentUser.uid)], (data) => {
      if (data && data.length > 0) {
        dataUser.value = data
        userName.value = data[0].displayName || 'User'
        userEmail.value = data[0].email || ''
      }
    }, null, true)
  }
}


onMounted(() => {
  fetchUserAuthentication()
  fetchAllPlans()
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* Gradient Animation */
.animate-gradient {
  animation: gradient 15s ease infinite;
}

@keyframes gradient {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.animate-bounce-slow {
  animation: bounceSlow 3s infinite;
}

@keyframes bounceSlow {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

/* Router Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>